import { useEffect, useState } from "react";
import { Alert, Box, Button, Paper, TextField, Typography } from "@mui/material";
import StringListEditor from "./StringListEditor";

export type ObjectFieldSpec =
  | { key: string; label: string; type: "text" | "multiline" }
  | { key: string; label: string; type: "stringList" };

export default function ObjectForm({
  title,
  fields,
  value,
  onSave,
}: {
  title: string;
  fields: ObjectFieldSpec[];
  value: Record<string, any>;
  onSave: (value: Record<string, any>) => Promise<void>;
}) {
  const [form, setForm] = useState<Record<string, any>>(value);
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(value);
  }, [value]);

  const setField = (key: string, fieldValue: unknown) => {
    setForm((prev) => ({ ...prev, [key]: fieldValue }));
    setStatus("idle");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      setStatus("saved");
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        {title}
      </Typography>
      {status === "saved" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Lagret.
        </Alert>
      )}
      {status === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Lagring feilet.
        </Alert>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {fields.map((f) => {
          if (f.type === "stringList") {
            return (
              <StringListEditor
                key={f.key}
                label={f.label}
                items={form[f.key] ?? []}
                onChange={(items) => setField(f.key, items)}
              />
            );
          }
          return (
            <TextField
              key={f.key}
              label={f.label}
              value={form[f.key] ?? ""}
              onChange={(e) => setField(f.key, e.target.value)}
              multiline={f.type === "multiline"}
              minRows={f.type === "multiline" ? 3 : 1}
              fullWidth
            />
          );
        })}
      </Box>
      <Button sx={{ mt: 2 }} variant="contained" onClick={handleSave} disabled={saving}>
        Lagre
      </Button>
    </Paper>
  );
}
